import {
  ArrowLeft,
  Calculator,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  Download,
  Edit,
  ExternalLink,
  FileText,
  Info,
  Plus,
  PlusCircle,
  Share2,
  Trash2,
  TrendingUp,
  User,
  Copy,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Truck, Wifi } from "react-bootstrap-icons";
import { Link, useLocation, useParams } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import { responseError } from "../../helpers/responseHandler";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  useAddIftaTripEntryMutation,
  useEditIftaTripEntryMutation,
  useGetFuelRatesQuery,
  useGetIftaDetailQuery,
  useRemoveIftaTripEntryMutation,
  useUpdateStatusIftaMutation,
} from "../../redux/services/IftaComplianceService";
import { deleteConfirmation, sweetAlert } from "../../utils";
import SummaryCard from "./components/SummaryCard";
import AddIftaEntryModal from "./AddIftaEntryModal";
import IftaReport from "./components/IftaReport";

/* ================= STATIC DATA ================= */

const MOCK_REPORT = {
  id: 1,
  truckId: "TRK-104",
  quarter: "q3",
  year: 2024,
  status: "In Progress",
  entries: [
    {
      id: 1,
      Created: "Jan 3, 7:46 PM",
      state: "WE",
      miles: 34,
      gallons: 34,
    },
    {
      id: 2,
      Created: "Jan 3, 7:46 PM",
      state: "HG",
      miles: 23,
      gallons: 1000,
    },
    {
      id: 3,
      Created: "Jan 3, 7:46 PM",
      state: "NM",
      miles: 600,
      gallons: 80,
    },
    {
      id: 4,
      Created: "Jan 3, 7:46 PM",
      state: "CO",
      miles: 420,
      gallons: 55,
    },
    {
      id: 5,
      Created: "Jan 3, 7:46 PM",
      state: "UT",
      miles: 850,
      gallons: 0,
    },
    {
      id: 6,
      Created: "Jan 3, 7:46 PM",
      state: "NV",
      miles: 300,
      gallons: 0,
    },
  ],
};

const MOCK_TAX_RATES = { TX: 0.2, OK: 0.19, MO: 0.17 };

const formatNumber = (n) => n.toLocaleString();
const formatCurrency = (n) => "$" + n.toFixed(2);

const getRowUI = (state) => {
  if (["WE", "HG"].includes(state)) {
    return {
      icon: <User size={18} className="me-2 text-secondary" />,
      taxClass: "text-success",
    };
  }

  if (["NM", "CO"].includes(state)) {
    return {
      icon: <FileText size={18} className="me-2 text-primary" />,
      taxClass: "text-primary",
    };
  }

  if (["UT", "NV"].includes(state)) {
    return {
      icon: <Wifi size={18} className="me-2 text-primary" />,
      taxClass: "text-primary",
    };
  }

  return {
    icon: null,
    taxClass: "text-danger",
  };
};

const QUARTERS = [
  { id: "q1", label: "1st Quarter (Jan - Mar)" },
  { id: "q2", label: "2nd Quarter (Apr - Jun)" },
  { id: "q3", label: "3rd Quarter (Jul - Sep)" },
  { id: "q4", label: "4th Quarter (Oct - Dec)" },
];

const getQuarterDateRange = (id) =>
  QUARTERS.find((q) => q.id === id)?.label || "";

const totalMiles = MOCK_REPORT.entries.reduce((a, b) => a + b.miles, 0);
const totalGallons = MOCK_REPORT.entries.reduce((a, b) => a + b.gallons, 0);
const mpg = totalGallons ? (totalMiles / totalGallons).toFixed(1) : 0;

const grandTotalDue = MOCK_REPORT.entries.reduce(
  (a, e) => a + (MOCK_TAX_RATES[e.state] || 0) * e.gallons,
  0,
);

/* ================= COMPONENT ================= */

export default function IftaComplianceViewStatic() {
  const params = useParams();
  const location = useLocation();
  const vehicle = location.state?.vehicle;

  const { data, isLoading } = useGetFuelRatesQuery();
  const {
    data: data2,
    isLoading: isLoading2,
    isFetching,
    refetch,
  } = useGetIftaDetailQuery({ id: params?.id });
  const [mutation, { isLoading: isStatusLoading }] =
    useUpdateStatusIftaMutation();
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [editRow, setEditRow] = useState({});
  const [completed, setCompleted] = useState(false);
  const [activeTab, setActiveTab] = useState("input");
  const [showExplanation, setShowExplanation] = useState(null);
  const [copied, setCopied] = useState(false);

  const [editingRow, setEditingRow] = useState(null);

  const [addRequest, { isLoading: isAddRow }] = useAddIftaTripEntryMutation();
  const [editRequest, { isLoading: isEditingRow }] =
    useEditIftaTripEntryMutation();
  const [removeRequest] = useRemoveIftaTripEntryMutation();

  const QUARTERLY_RATES = useMemo(
    () => data?.response?.data || {},
    [data?.response?.data],
  );
  const iftaDetails = useMemo(
    () => data2?.response?.data || {},
    [data2?.response?.data],
  );
  // --- STATE ---
  const [rows, setRows] = useState([]);

  const formatNum = (val, decimals = 2) => {
    if (val === null || val === undefined || isNaN(val)) return `0`;
    return val?.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };
  // --- RENDER HELPERS ---
  const formatMoney = (val) => {
    if (val === null || val === undefined || isNaN(val)) return `$0.00`;
    const absVal = Math.abs(val);
    const formatted = absVal.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
    return val < 0 ? `(${formatted})` : formatted;
  };

  // --- ACTIONS ---
  const addRow = async () => {
    const newId = Math.random().toString(36).substr(2, 9);

    await addRequest({
      i_f_t_a_filling_id: params?.id,
      state: "ALabama",
      miles: "0",
      gallons: "0",
      tax_rate_id: "1",
    })
      .unwrap()
      .then((res) => {
        console.log("res", res);
        if (res?.status) {
          sweetAlert(res?.message, "success");
          setRows([
            ...rows,
            {
              id: newId,
              jurisdiction: "ALabama",
              miles: "0",
              taxPaidGallons: "0",
            },
          ]);
          refetch();
        }
      })
      .catch((err) => {
        console.log("err", err);
        responseError(err);
      });
  };

  const removeRow = (row) => {
    deleteConfirmation(() => {
      removeRequest(row?.id)
        .unwrap()
        .then((res) => {
          console.log("res", res);
          if (res?.status) {
            sweetAlert(res?.message, "success");
            setRows(rows.filter((r) => r.id !== row.id));
            refetch();
          }
        })
        .catch((err) => {
          console.log("err", err);
          responseError(err);
        });
    });
  };

  const startEdit = (row) => {
    setEditRow({ ...row }); // copy row to temp state
  };

  const saveEdit = async (id, rates) => {
    const rate = rates?.find((r) => r.state_name == editRow?.jurisdiction);
    const data = {
      ...editRow,
    };
    data["state"] = data?.jurisdiction;
    data["gallons"] = data?.taxPaidGallons;

    if (rate?.id) {
      data["tax_rate_id"] = rate?.id;
    }

    delete data["jurisdiction"];
    delete data["taxPaidGallons"];

    await editRequest({ i_f_t_a_filling_id: params?.id, id: id, ...data })
      .unwrap()
      .then((res) => {
        console.log("res", res);
        if (res?.status) {
          sweetAlert(res?.message, "success");
          // setRows([...rows, { id: newId, jurisdiction: 'AL', miles: '0', taxPaidGallons: '0' }]);
          setRows((prev) =>
            prev.map((row) => (row.id === id ? { ...editRow } : row)),
          );
          setEditRow(null);
        }
      })
      .catch((err) => {
        console.log("err", err);
        responseError(err);
      });
  };

  useEffect(() => {
    if (iftaDetails?.entries && iftaDetails?.entries.length > 0) {
      const mappedRows = iftaDetails?.entries.map((entry) => ({
        id: entry?.id?.toString(),
        jurisdiction: entry?.state,
        miles: entry?.miles,
        taxPaidGallons: entry?.gallons,
        tax_rate: entry?.tax_rate,
        surcharge: entry?.surcharge,
      }));
      setRows(mappedRows);
    }
  }, [iftaDetails?.entries]);

  // --- CORE IFTA ALGORITHM ---
  const calculation = useMemo(() => {
    // Helper to safely parse numbers from inputs (which might be strings)
    const getVal = (v) => {
      if (typeof v === "number") return v;
      if (!v) return 0;
      const parsed = parseFloat(v);
      return isNaN(parsed) ? 0 : parsed;
    };

    // 1. Aggregate totals
    const totalMiles = rows?.reduce((sum, r) => sum + getVal(r.miles), 0);
    const totalGallonsPurchased = rows?.reduce(
      (sum, r) => sum + getVal(r.taxPaidGallons),
      0,
    );

    // 2. Calculate Fleet MPG (Avoid division by zero)
    // Standard practice: If 0 gallons, MPG is usually set to a standard 4.00 or flagged for audit.
    // Here we return 0 to show "Invalid" state initially.
    const mpg =
      totalGallonsPurchased > 0 ? totalMiles / totalGallonsPurchased : 0;

    // 3. Process each jurisdiction (aggregating duplicates if user entered same state twice)
    const jurisdictionMap = new Map();

    rows.forEach((row) => {
      const code = row.jurisdiction;
      // SAFEGUARD: Ensure rates exist for this code to prevent crashes
      const rates =
        QUARTERLY_RATES?.length > 0 &&
        QUARTERLY_RATES?.find((r) => r.state_name == code);

      if (!rates) {
        // If we encounter a jurisdiction not in our DB, we skip or handle gracefully to prevent crash
        return;
      }

      const current = jurisdictionMap?.get(code) || {
        jurisdiction: code,
        totalMiles: 0,
        taxableGallons: 0,
        taxPaidGallons: 0,
        netTaxableGallons: 0,
        taxRate: rates.rate,
        taxDue: 0,
        surchargeRate: rates.surcharge,
        surchargeDue: 0,
        totalDue: 0,
      };

      current.totalMiles += getVal(row?.miles);
      current.taxPaidGallons += getVal(row?.taxPaidGallons);
      jurisdictionMap.set(code, current);
    });

    // 4. Finalize calculations per jurisdiction
    const results = [];
    let grandTotalDue = 0;

    jurisdictionMap.forEach((data, code) => {
      // Step A: Taxable Gallons = Miles / MPG
      const taxableGallons = mpg > 0 ? data?.totalMiles / mpg : 0;

      // Step B: Net Taxable = Taxable - Paid
      const netTaxableGallons = taxableGallons - data?.taxPaidGallons;

      // Step C: Tax Due = Net * Rate
      const taxDue = netTaxableGallons * data?.taxRate;

      // Step D: Surcharges (Calculated on Taxable Gallons usually, varies by state but generally on usage)
      // Note: Most jurisdictions apply surcharge to Taxable Gallons (fuel consumed), not net.
      const surchargeDue = taxableGallons * data?.surchargeRate;

      data.taxableGallons = taxableGallons;
      data.netTaxableGallons = netTaxableGallons;
      data.taxDue = taxDue;
      data.surchargeDue = surchargeDue;
      data.totalDue = taxDue + surchargeDue;

      grandTotalDue += data.totalDue;
      results.push(data);
    });

    return {
      summary: {
        totalMiles,
        totalGallons: totalGallonsPurchased,
        mpg,
        grandTotal: grandTotalDue,
      },
      details: results?.sort((a, b) =>
        a.jurisdiction.localeCompare(b.jurisdiction),
      ),
    };
  }, [rows]);

  const handleAddEntry = async (data) => {
    const rate = QUARTERLY_RATES.find((r) => r.id == data.jurisdiction);

    await addRequest({
      i_f_t_a_filling_id: params?.id,
      state: rate?.state_name,
      miles: data.miles,
      gallons: data.taxPaidGallons,
      tax_rate_id: rate?.id,
    })
      .unwrap()
      .then(() => {
        sweetAlert("Entry added successfully", "success");
        setShowAddModal(false);
        refetch();
      })
      .catch(responseError);
  };
  const handleSubmitEntry = async (data) => {
    const rate = QUARTERLY_RATES.find((r) => r.id == data.jurisdiction);

    const payload = {
      i_f_t_a_filling_id: params?.id,
      state: rate?.state_name,
      miles: data.miles,
      gallons: data.taxPaidGallons,
      tax_rate_id: rate?.id,
    };

    try {
      if (data?.id) {
        // ✅ EDIT
        await editRequest({ id: data.id, ...payload }).unwrap();
        sweetAlert("Entry updated successfully", "success");
      } else {
        // ✅ ADD
        await addRequest(payload).unwrap();
        sweetAlert("Entry added successfully", "success");
      }

      setShowEntryModal(false);
      setEditingRow(null);
      refetch();
    } catch (err) {
      responseError(err);
    }
  };
  const url = `${window.location.origin}/ifta-public-url/${iftaDetails?.url_slug}`;
  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const [loading, setLoading] = React.useState(false);

  const [loadingTwo, setLoadingTwp] = React.useState(false);
  const generatePDF = () => {
    setLoadingTwp(true);

    const report = document.getElementById("report-content");

    html2canvas(report, {
      scale: 2,
      scrollY: -window.scrollY,
      useCORS: true,
      windowWidth: document.body.scrollWidth,
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");

        const imgWidth = 210;
        const pageHeight = 297;
        console.log("can==", canvas.height * imgWidth, canvas.width);
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save("IftaCompliance.pdf");
      })
      .finally(() => {
        setLoadingTwp(false);
      });
  };

 const handleStatusUpdate = async (status) => {
  try {
    await mutation({
      id: params?.id,
      status: status,
    }).unwrap();

    setCompleted(status === 1);
    sweetAlert("Success", "Status updated successfully", "success");
    refetch();
  } catch (err) {
    responseError(err);
  }
};


useEffect(() => {
  const status = data2?.response?.data?.status;
  setCompleted(status === "COMPLETED");
}, [data2]);


  if (isLoading || isLoading2 || loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        name="IFTA Compliance View"
        description="Manage fuel tax filings and driver reports."
      />

      <div
        className="container-fluid my-4 bg-white py-4"
        style={{ fontSize: "18px", lineHeight: "1.6" }}
      >
        {/* ===== HEADER ===== */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-3 border-bottom gap-3">
          <div className="d-flex align-items-center gap-2">
            <Link
              to="/ifta-compliance"
              className="btn btn-light p-2 rounded-circle"
            >
              <ArrowLeft size={22} />
            </Link>

            <div>
              <h5 className="fw-bold mb-1">
                TRK-{iftaDetails?.id?.toString()?.padStart(3, "0")} -{" "}
                {iftaDetails?.year} Q{iftaDetails?.quarter}
              </h5>
              <div className="d-flex align-items-center gap-2">
                <span
                  className={`badge ${completed ? "bg-success" : "bg-warning"}`}
                >
                  {completed ? "Completed" : "In Progress"}
                </span>

                <span className="text-muted">
                  {getQuarterDateRange(`q${iftaDetails?.quarter}`)}
                </span>
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            {!completed && (
              <>
                <button
                  className="btn btn-outline-success"
                  onClick={() => handleStatusUpdate(1)}
                  disabled={isStatusLoading}
                >
                  <CheckSquare size={18} /> Mark as Complete
                </button>

                <Link
                  to={`/ifta-public-url/${params?.id}`}
                  className="btn btn-outline-primary"
                >
                  <Share2 size={18} /> Driver Public
                </Link>
              </>
            )}

            {completed && (
              <button
                className="btn btn-outline-secondary"
                onClick={() => handleStatusUpdate(0)}
                disabled={isStatusLoading}
              >
                Re-open Filing
              </button>
            )}

            <button
              onClick={generatePDF}
              disabled={loadingTwo}
              className="btn btn-outline-dark"
            >
              <Download size={18} className="me-1" />
              {loadingTwo ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Exporting...
                </>
              ) : (
                "Export PDF"
              )}
            </button>
          </div>
        </div>

        {/* ===== MAIN RESPONSIVE LAYOUT ===== */}
        <div className="row g-4">
          {/* LEFT SIDE */}
          <div className="col-12 col-lg-8">
            {/* Header */}
            <div className="mb-4 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
              {/* Left Side */}
              <div>
                <h1 className="fw-bold d-flex align-items-center gap-2 text-dark fs-3">
                  <Truck size={32} className="text-primary" />
                  IFTA Compliance Calculator
                </h1>
                <p className="text-muted small pt-2">
                  This Calculator aims to provide estimations based on standard
                  IFTA-101 logic. Rates used are illustrative 2025 Q4 samples.
                  Official returns should verify rates with IFTA Inc.
                </p>
              </div>

              {/* Tabs */}
              <div className="d-flex bg-white p-1 rounded-2 shadow-sm border">
                <button
                  onClick={() => setActiveTab("input")}
                  className={`btn btn-sm fw-medium px-4 w-100 w-md-auto text-nowrap ${
                    activeTab === "input"
                      ? "btn-primary shadow"
                      : "btn-light text-secondary"
                  }`}
                >
                  Data Entry
                </button>

                <button
                  onClick={() => setActiveTab("report")}
                  className={`btn btn-sm fw-medium px-4 w-100 w-md-auto ms-2  text-nowrap ${
                    activeTab === "report"
                      ? "btn-primary shadow"
                      : "btn-light text-secondary"
                  }`}
                >
                  Tax Report
                </button>
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-12 col-md-3">
                <SummaryCard
                  label="Total Distance"
                  value={`${formatNum(calculation?.summary?.totalMiles, 0)} mi`}
                  color="blue"
                />
              </div>

              <div className="col-12 col-md-3">
                <SummaryCard
                  label="Fuel Purchased"
                  value={`${formatNum(calculation?.summary?.totalGallons, 0)} gal`}
                  color="indigo"
                />
              </div>

              <div className="col-12 col-md-3">
                <SummaryCard
                  label="Fleet Average MPG"
                  value={formatNum(calculation?.summary?.mpg, 1)}
                  sublabel="Calculated from Total Miles / Total Gal"
                  color="emerald"
                  highlight
                />
              </div>

              <div className="col-12 col-md-3">
                <SummaryCard
                  label="Estimated IFTA Tax"
                  value={formatMoney(calculation?.summary?.grandTotal)}
                  color={
                    calculation?.summary?.grandTotal > 0 ? "amber" : "green"
                  }
                  sublabel={
                    calculation?.summary?.grandTotal < 0
                      ? "Credit Owed to You"
                      : "Balance Due"
                  }
                />
              </div>
            </div>

            <div className="bg-white rounded-4 shadow-sm border overflow-hidden">
              {activeTab === "input" && (
                <div className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-semibold mb-0">Trip & Fuel Logs</h5>

                    {/* <button
                      onClick={addRow}
                      className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                    >
                      <PlusCircle size={16} /> Add Row
                    </button> */}
                  
                    <button
                      className="btn btn-primary"
                      disabled={completed}
                      onClick={() => setShowEntryModal(true)}
                    >
                      Add Trip
                    </button>
               
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light text-uppercase small text-muted">
                        <tr>
                          <th>Jurisdiction</th>
                          <th className="text-end">Tax Rate</th>

                          <th className="text-end">Total Miles</th>
                          <th className="text-end">Tax-Paid Gallons</th>
                          <th style={{ width: 60 }}></th>
                        </tr>
                      </thead>

                      <tbody>
                        {isFetching ? (
                          <tr>
                            <td colSpan={4}>
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{ height: 200 }}
                              >
                                <i className="ri-loader-2-fill fs-25 rotation"></i>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          rows?.map((row) => {
                            const isEditing = editRow?.id == row?.id;
                            return (
                              <tr key={row.id}>
                                {console.log("row", row)}
                                <td>
                                  {isEditing ? (
                                    <select
                                      value={editRow.jurisdiction}
                                      onChange={(e) =>
                                        setEditRow({
                                          ...editRow,
                                          jurisdiction: e.target.value,
                                        })
                                      }
                                      className="form-select"
                                    >
                                      <option value="">Select State</option>
                                      {/* <option value="all">All</option> */}
                                      {QUARTERLY_RATES.map((item) => (
                                        <option
                                          key={item.id}
                                          value={item?.state_name}
                                        >
                                          {item.state_code} – {item.state_name}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    row?.jurisdiction
                                  )}
                                </td>
                                <td className="text-end">
                                  {isEditing ? (
                                    <input
                                      type="number"
                                      value={editRow.tax_rate}
                                      onChange={(e) =>
                                        setEditRow({
                                          ...editRow,
                                          tax_rate: e.target.value,
                                        })
                                      }
                                      className="form-control text-end"
                                    />
                                  ) : (
                                    row?.tax_rate
                                  )}
                                </td>

                                <td className="text-end">
                                  {isEditing ? (
                                    <input
                                      type="number"
                                      value={editRow.miles}
                                      onChange={(e) =>
                                        setEditRow({
                                          ...editRow,
                                          miles: e.target.value,
                                        })
                                      }
                                      className="form-control text-end"
                                    />
                                  ) : (
                                    row?.miles
                                  )}
                                </td>

                                <td className="text-end">
                                  {isEditing ? (
                                    <input
                                      type="number"
                                      value={editRow.taxPaidGallons}
                                      onChange={(e) =>
                                        setEditRow({
                                          ...editRow,
                                          taxPaidGallons: e.target.value,
                                        })
                                      }
                                      className="form-control text-end"
                                    />
                                  ) : (
                                    row?.taxPaidGallons
                                  )}
                                </td>

                                <td
                                  className="text-end d-flex align-items-center justify-content-end gap-2"
                                  width={180}
                                >
                                  {isEditing ? (
                                    <button
                                      onClick={() =>
                                        saveEdit(row?.id, QUARTERLY_RATES)
                                      }
                                      className="btn btn-outline-success d-flex align-items-center justify-content-center rounded-2"
                                      style={{ height: 35 }}
                                      disabled={isEditingRow}
                                    >
                                      {isEditingRow ? (
                                        <>
                                          <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                          ></span>
                                          Save
                                        </>
                                      ) : (
                                        "Save"
                                      )}
                                    </button>
                                  ) : (
                                    <button
                                    disabled={completed}
                                      onClick={() => {
                                        setEditingRow({
                                          ...row,
                                          tax_rate_id: QUARTERLY_RATES.find(
                                            (r) =>
                                              r.state_name === row.jurisdiction,
                                          )?.id,
                                        });
                                        setShowEntryModal(true);
                                      }}
                                      className="btn btn-outline-secondary"
                                    >
                                      Edit
                                    </button>
                                  )}
                                  <button
                                  disabled={completed}
                                    onClick={() => removeRow(row)}
                                    className="btn btn-outline-danger d-flex align-items-center justify-content-center rounded-2"
                                    style={{ width: 35, height: 35 }}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {rows?.length === 0 && (
                    <div className="text-center text-muted py-5">
                      No trip data entered. Add a row to begin.
                    </div>
                  )}
                </div>
              )}

              {activeTab === "report" && (
                <div>
                  {calculation?.summary?.mpg === 0 ? (
                    <div className="p-5 text-center">
                      <div
                        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-warning-subtle text-warning mb-3"
                        style={{ width: 64, height: 64 }}
                      >
                        <Info size={32} />
                      </div>
                      <h5>Insufficient Data</h5>
                      <p className="text-muted">
                        We cannot calculate MPG because total fuel purchased is
                        zero.
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered align-middle mb-0">
                        <thead className="table-light small">
                          <tr>
                            <th></th>
                            <th>Jurisdiction</th>
                            <th className="text-end">Tax Rate</th>
                            <th className="text-end">Miles</th>
                            <th className="text-end bg-primary-subtle">
                              Taxable Gal
                            </th>
                            <th className="text-end">Paid Gal</th>
                            <th className="text-end">Net Gal</th>
                            <th className="text-end">Tax</th>
                            <th className="text-end">Surcharge</th>
                            <th className="text-end text-success fw-bold">
                              Overpaid
                            </th>
                            <th className="text-end fw-bold">Balance Due</th>
                          </tr>
                        </thead>

                        <tbody>
                          {calculation?.details?.map((row) => (
                            <React.Fragment key={row?.jurisdiction}>
                              <tr
                                className={
                                  showExplanation === row?.jurisdiction
                                    ? "table-primary"
                                    : ""
                                }
                              >
                                <td className="text-center">
                                  <button
                                    onClick={() =>
                                      setShowExplanation(
                                        showExplanation === row.jurisdiction
                                          ? null
                                          : row.jurisdiction,
                                      )
                                    }
                                    className="btn btn-sm btn-link"
                                  >
                                    {showExplanation === row.jurisdiction ? (
                                      <ChevronUp size={14} />
                                    ) : (
                                      <ChevronDown size={14} />
                                    )}
                                  </button>
                                </td>

                                <td>
                                  <strong>{row?.jurisdiction}</strong>{" "}
                                  {/* <small className="text-muted">({QUARTERLY_RATES?.length > 0 && QUARTERLY_RATES?.find(r => r.state_name == row?.jurisdiction)?.name})</small> */}
                                </td>

                                <td className="text-end">
                                  {parseFloat(row?.taxRate)?.toFixed(4)}
                                </td>
                                <td className="text-end">
                                  {formatNum(row?.totalMiles)}
                                </td>
                                <td className="text-end bg-primary-subtle fw-semibold">
                                  {formatNum(row?.taxableGallons)}
                                </td>
                                <td className="text-end">
                                  {formatNum(row?.taxPaidGallons)}
                                </td>
                                <td className="text-end">
                                  {formatNum(row?.netTaxableGallons)}
                                </td>
                                <td className="text-end">
                                  {formatMoney(row?.taxDue)}
                                </td>
                                <td className="text-end text-muted">
                                  {row?.surchargeDue > 0
                                    ? formatMoney(row?.surchargeDue)
                                    : "-"}
                                </td>

                                <td className="text-end text-success">
                                  {row?.totalDue < 0
                                    ? formatMoney(Math.abs(row?.totalDue))
                                    : "-"}
                                </td>

                                <td
                                  className={`text-end fw-bold ${row?.totalDue > 0 ? "text-warning" : "text-muted"}`}
                                >
                                  {row?.totalDue > 0
                                    ? formatMoney(row?.totalDue)
                                    : "-"}
                                </td>
                              </tr>

                              {showExplanation === row?.jurisdiction && (
                                <tr className="bg-light">
                                  <td
                                    colSpan={11}
                                    className="p-3 border-bottom border-primary-subtle"
                                  >
                                    <div className="bg-white border border-primary-subtle rounded-3 p-4 shadow-sm">
                                      <h6 className="text-primary text-uppercase fw-bold mb-3 d-flex align-items-center gap-2">
                                        <Calculator size={14} /> Calculation
                                        Logic for {row?.jurisdiction}
                                      </h6>

                                      <div className="row g-4 small">
                                        {/* STEP 1 */}
                                        <div className="col-12 col-md-4">
                                          <div className="fw-semibold text-dark h6">
                                            1. Taxable Gallons
                                          </div>
                                          <div className="text-muted h6">
                                            How much fuel did you burn here?
                                          </div>

                                          <div className="bg-light border rounded-2 p-2 font-monospace mt-2 h6">
                                            {formatNum(row?.totalMiles, 0)} mi ÷{" "}
                                            {formatNum(
                                              calculation?.summary?.mpg,
                                              2,
                                            )}{" "}
                                            MPG ={" "}
                                            <span className="fw-bold text-primary">
                                              {formatNum(row?.taxableGallons)}{" "}
                                              gal
                                            </span>
                                          </div>
                                        </div>

                                        {/* STEP 2 */}
                                        <div className="col-12 col-md-4">
                                          <div className="fw-semibold text-dark h6">
                                            2. Net Taxable
                                          </div>
                                          <div className="text-muted small h6">
                                            Burned vs. Purchased
                                          </div>

                                          <div className="bg-light border rounded-2 p-2 font-monospace mt-2 h6">
                                            {formatNum(row?.taxableGallons)}{" "}
                                            used −{" "}
                                            {formatNum(row?.taxPaidGallons)}{" "}
                                            bought ={" "}
                                            <span
                                              className={`fw-bold ${row?.netTaxableGallons > 0 ? "text-warning" : "text-success"}`}
                                            >
                                              {formatNum(
                                                row?.netTaxableGallons,
                                              )}{" "}
                                              gal
                                            </span>
                                          </div>
                                        </div>

                                        {/* STEP 3 */}
                                        <div className="col-12 col-md-4">
                                          <div className="fw-semibold text-dark h6">
                                            3. Final Tax
                                          </div>
                                          <div className="text-muted small h6">
                                            Apply Rate ({row.taxRate}) +
                                            Surcharge
                                          </div>

                                          <div className="bg-light border rounded-2 p-2 font-monospace h6 mt-2">
                                            ({formatNum(row?.netTaxableGallons)}{" "}
                                            × {row?.taxRate})
                                            {row.surchargeRate > 0 &&
                                              ` + (${formatNum(row?.taxableGallons)} × ${row?.surchargeRate})`}{" "}
                                            ={" "}
                                            <span
                                              className={`fw-bold ${row?.totalDue > 0 ? "text-warning" : "text-success"}`}
                                            >
                                              {formatMoney(row?.totalDue)}
                                            </span>
                                          </div>

                                          {row.surchargeRate > 0 && (
                                            <div className="text-warning small mt-1">
                                              * Includes {row?.jurisdiction}{" "}
                                              Surcharge
                                            </div>
                                          )}

                                          {row.totalDue < 0 && (
                                            <div className="text-success fw-bold small mt-1">
                                              CREDIT: You overpaid{" "}
                                              {formatMoney(
                                                Math.abs(row?.totalDue),
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="report-offscreen">
              <IftaReport
                calculation={calculation}
                showExplanation={showExplanation}
                setShowExplanation={setShowExplanation}
                formatNum={formatNum}
                formatMoney={formatMoney}
                iftaDetails={iftaDetails}
                params={params}
                vehicle={vehicle}
                getQuarterDateRange={getQuarterDateRange}
              />
            </div>

            {/* <div className="card mb-4">
              {!completed && (
                <div className="card-header d-flex justify-content-between">
                  <h5 className="mb-0">Add Trip Entries</h5>
                  <span className="badge bg-light border text-muted">
                    Manual & Auto-Calc
                  </span>
                </div>
              )}

              {!completed && (
                <div className="bg-primary p-3">
                  <div className="row g-2 align-items-end">
                    <div className="col-4 col-md-2">
                      <label className="text-white small fw-bold">
                        State
                      </label>
                      <input className="form-control form-control-sm" />
                    </div>
                    <div className="col">
                      <label className="text-white small fw-bold">
                        Miles
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                      />
                    </div>
                    <div className="col">
                      <label className="text-white small fw-bold">
                        Gallons
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                      />
                    </div>
                    <div className="col-auto">
                      <button className="btn btn-light btn-sm">
                        <Plus size={16} /> Add Trip
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="card-header d-flex justify-content-between">
                <h5 className="mb-0">Trip Entries</h5>

              </div>
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Created</th>
                      <th>State</th>
                      <th className="text-end">Miles</th>
                      <th className="text-end">Gallons</th>
                      <th className="text-end">Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MOCK_REPORT.entries.map((e) => {
                      const ui = getRowUI(e.state);
                      const tax =
                        (MOCK_TAX_RATES[e.state] || 0) * e.gallons;

                      return (
                        <tr key={e.id}>
                          <td>{e.Created}</td>
                          <td className="d-flex align-items-center">
                            {ui.icon}
                            {e.state}
                          </td>
                          <td className="text-end">
                            {formatNumber(e.miles)}
                          </td>
                          <td className="text-end">
                            {formatNumber(e.gallons)}
                          </td>
                          <td
                            className={`text-end fw-bold ${ui.taxClass}`}
                          >
                            {formatCurrency(tax)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div> */}
            <p className="text-center text-muted small pt-2">
              This Calculator aims to provide estimations based on standard
              IFTA-101 logic. <br />
              Rates used are illustrative 2025 Q4 samples. Official returns
              should verify rates with IFTA Inc.
            </p>
          </div>

          {/* RIGHT SIDE */}
          <div className="col-12 col-lg-4">
            {!completed ? (
              <div className="card mb-3 bg-primary bg-opacity-10 border-primary">
                <div className="card-body">
                  <h6 className="fw-bold">
                    <ExternalLink size={18} /> Public Driver Link
                  </h6>
                  <div className="d-flex gap-0">
                    <input
                      readOnly
                      className="form-control mb-2 custome_input"
                      value={url}
                    />
                    <button
                      onClick={handleCopy}
                      className="btn btn-light btn-sm text-wrap pb-0 custome_copy mb-2"
                    >
                      <Copy size={14} />
                      {copied ? "Copied!" : "Copy"}
                    </button>
                    <Link
                      to={`/ifta-public-url/${iftaDetails?.url_slug}`}
                      className="btn btn-light btn-sm text-wrap pb-0 custom_link mb-2"
                    >
                      Open
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-success bg-opacity-10  p-3 border border-success mb-3">
                <h6 className="text-success fw-bold d-flex align-items-center gap-2 text-white">
                  <CheckCircle size={20} /> Filing Ready
                </h6>
                <p className="text-success mt-1 text-white">
                  This report is marked as complete. You can now download the
                  PDF and file it with your jurisdiction.
                </p>
              </div>
            )}

            <div className="card">
              <div className="card-body">
                <h6 className="fw-bold mb-3">Details</h6>
                <select className="form-select mb-2" disabled={completed}>
                  <option>{vehicle || "All Fleet"}</option>
                </select>

                <select className="form-select mb-3" disabled={completed}>
                  <option>Diesel</option>
                  <option>Gasoline</option>
                </select>

                {/* <button
                  disabled={completed}
                  className="btn btn-outline-secondary w-100 mb-2"
                >
                  <FileText size={16} /> Upload Fuel CSV
                </button>
                <div className="download text-center mt-0 mb-2">
                  <Link to="#" className=" text-13px  text-center text-primary">
                    Download Sample CSV Template
                  </Link>
                </div>
                <button
                  disabled={completed}
                  className="btn btn-outline-secondary w-100"
                >
                  <TrendingUp size={16} /> Sync GPS Miles
                </button> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddIftaEntryModal
        show={showEntryModal}
        onClose={() => {
          setShowEntryModal(false);
          setEditingRow(null);
        }}
        onAdd={handleAddEntry}
        onSubmit={handleSubmitEntry}
        initialData={editingRow}
        QUARTERLY_RATES={QUARTERLY_RATES}
        isLoading={isAddRow || isEditingRow}
      />
    </>
  );
}
